// src/utils/middleware/domainMiddleware.ts
import { useEffect, useState } from 'react';

interface RequestHeaders {
  referer?: string;
  origin?: string;
}

export class DomainMiddleware {
  private static instance: DomainMiddleware;
  private allowedDomains: string[];

  private constructor() {
    // Load allowed domains from environment variables
    const defaultDomains = ['localhost', '127.0.0.1'];
    
    this.allowedDomains = import.meta.env.VITE_ALLOWED_DOMAINS
      ? [...defaultDomains, ...import.meta.env.VITE_ALLOWED_DOMAINS.split(',').map((domain: string) => domain.trim())]
      : defaultDomains;
  }

  public static getInstance(): DomainMiddleware {
    if (!DomainMiddleware.instance) {
      DomainMiddleware.instance = new DomainMiddleware();
    }
    return DomainMiddleware.instance;
  }

  public validateDomain(): boolean {
    try {
      // Check if running in development
      if (import.meta.env.DEV) {
        return true;
      }

      // Get current domain
      const currentDomain = window.location.hostname;

      // Check if running in an iframe
      const isEmbedded = window !== window.parent;

      if (isEmbedded) {
        try {
          // Try to get parent URL directly
          const parentUrl = new URL(window.parent.location.href);
          const parentDomain = parentUrl.hostname;

          // Check both current domain and parent domain
          return this.isAllowedDomain(currentDomain) && this.isAllowedDomain(parentDomain);
        } catch (error) {
          // If we can't access parent URL (due to cross-origin restrictions),
          // try to get it from document.referrer
          if (document.referrer) {
            const referrerUrl = new URL(document.referrer);
            const referrerDomain = referrerUrl.hostname;
            return this.isAllowedDomain(currentDomain) && this.isAllowedDomain(referrerDomain);
          }
          // If we can't access parent URL and there's no referrer, reject
          return false;
        }
      }

      // If not embedded, just check current domain
      return this.isAllowedDomain(currentDomain);
    } catch (error) {
      return false;
    }
  }

  public validateRequest(headers: RequestHeaders): boolean {
    try {
      const referer = headers.referer ? new URL(headers.referer).hostname : null;
      const origin = headers.origin ? new URL(headers.origin).hostname : null;

      // Check referer and origin against allowed domains
      if (referer && this.isAllowedDomain(referer)) {
        return true;
      }
      
      if (origin && this.isAllowedDomain(origin)) {
        return true;
      }

      return false;
    } catch (error) {
      return false;
    }
  }

  private isAllowedDomain(domain: string): boolean {
    return this.allowedDomains.some((allowedDomain: string) => 
      domain === allowedDomain || domain.endsWith(`.${allowedDomain}`)
    );
  }

  // Get parent domain info
  public getParentDomainInfo(): { isEmbedded: boolean; parentDomain: string | null } {
    const isEmbedded = window !== window.parent;
    let parentDomain = null;

    if (isEmbedded) {
      try {
        // Try to get parent URL directly
        parentDomain = new URL(window.parent.location.href).hostname;
      } catch (error) {
        // If direct access fails, try referrer
        if (document.referrer) {
          parentDomain = new URL(document.referrer).hostname;
        }
      }
    }

    return { isEmbedded, parentDomain };
  }
}

// Custom hook for using domain validation
export const useDomainValidation = (): { 
  isValid: boolean; 
  isEmbedded: boolean; 
  parentDomain: string | null 
} => {
  const [validationState, setValidationState] = useState<{
    isValid: boolean;
    isEmbedded: boolean;
    parentDomain: string | null;
  }>({
    isValid: true,
    isEmbedded: false,
    parentDomain: null
  });

  useEffect(() => {
    const middleware = DomainMiddleware.getInstance();
    const { isEmbedded, parentDomain } = middleware.getParentDomainInfo();
    const valid = middleware.validateDomain();

    setValidationState({
      isValid: valid,
      isEmbedded,
      parentDomain
    });
  }, []);

  return validationState;
};

// Add type definitions for environment variables
declare global {
  interface ImportMetaEnv {
    readonly VITE_ALLOWED_DOMAINS?: string;
  }
}
